import classNames from 'classnames';
import { formatTrafficDays, getDayName, getFormattedDate } from 'shared/lib/format';
import { formatPrice } from 'shared/lib/formatting';
import type { BadgeProps } from 'shared/ui/Badge';
import { Badge } from 'shared/ui/Badge';

export type OldToNewValueProps = (
  | {
      type: 'text';
      oldValue?: string | null;
      newValue?: string | null;
    }
  | {
      type: 'textCapitalized';
      oldValue?: string | null;
      newValue?: string | null;
    }
  | {
      type: 'number';
      oldValue: number;
      newValue: number;
    }
  | {
      type: 'badge';
      oldValue: BadgeProps;
      newValue: BadgeProps;
    }
  | {
      type: 'money';
      oldValue: number | string;
      newValue: number | string;
    }
  | {
      type: 'bidOrBidTiers';
      oldValue: number | number[];
      newValue: number | number[];
    }
  | {
      type: 'isActive';
      oldValue: boolean;
      newValue: boolean;
    }
  | {
      type: 'isApplied';
      oldValue: boolean;
      newValue: boolean;
    }
  | {
      type: 'trafficDays';
      oldValue: string;
      newValue: string;
    }
  | {
      type: 'pausingReasons';
      oldValue: string;
      newValue: string;
    }
) & { showInTwoLines?: boolean; effectiveAt?: string };

const formatTextValue = (value?: string | null) => value || 'No data is available';

const formatNumberValue = (value: number) => new Intl.NumberFormat('en-US').format(value);

const formatBidOrBidTiersValue = (value: number | number[]) =>
  Array.isArray(value) ? value.map((bid) => `${formatPrice(bid)}`).join(' / ') || '' : formatPrice(value);

export function OldToNewValue({ type, oldValue, newValue, showInTwoLines, effectiveAt }: OldToNewValueProps) {
  let formattedOldValue;
  let formattedNewValue;

  switch (type) {
    case 'text':
      formattedOldValue = formatTextValue(oldValue);
      formattedNewValue = formatTextValue(newValue);
      break;
    case 'textCapitalized':
      formattedOldValue = <span className="capitalize">{formatTextValue(oldValue)}</span>;
      formattedNewValue = <span className="capitalize">{formatTextValue(newValue)}</span>;
      break;
    case 'number':
      formattedOldValue = formatNumberValue(oldValue);
      formattedNewValue = formatNumberValue(newValue);
      break;
    case 'badge':
      formattedOldValue = <Badge {...oldValue} />;
      formattedNewValue = <Badge {...newValue} />;
      break;
    case 'money':
      formattedOldValue = formatPrice(oldValue);
      formattedNewValue = formatPrice(newValue);
      break;
    case 'bidOrBidTiers':
      formattedOldValue = formatBidOrBidTiersValue(oldValue);
      formattedNewValue = formatBidOrBidTiersValue(newValue);
      break;
    case 'isActive':
      formattedOldValue = (
        <>
          <span className="mr-[6px] font-semibold">Is active:</span>
          {oldValue ? 'Yes' : 'No'}
        </>
      );
      formattedNewValue = (
        <>
          <span className="mr-[6px] font-semibold">Is active:</span>
          {newValue ? 'Yes' : 'No'}
        </>
      );
      break;
    case 'isApplied':
      formattedOldValue = oldValue ? 'Applied' : 'Not applied';
      formattedNewValue = newValue ? 'Applied' : 'Not applied';
      break;
    case 'trafficDays':
      formattedOldValue = formatTrafficDays(oldValue, true);
      formattedNewValue = formatTrafficDays(newValue, true);
      break;
    case 'pausingReasons':
      formattedOldValue = (
        <>
          <span className="font-semibold">Pausing reason: </span>
          {oldValue}
        </>
      );
      formattedNewValue = <span className="font-semibold">{newValue}</span>;
      break;
    default:
      break;
  }

  return (
    <div
      data-testid="TestId__single-value-update"
      className={classNames('text-slate-900 text-xs', {
        'flex items-center': type === 'badge',
      })}
    >
      {formattedOldValue}
      <span className="inline-block w-6 text-center">{'>'}</span>
      <span
        className={classNames({
          'font-semibold': type !== 'badge' && type !== 'isActive',
          block: showInTwoLines,
        })}
      >
        {formattedNewValue}
      </span>
      {effectiveAt && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <span className="font-semibold">Effective at: </span>
          <span className="text-xs text-slate-900">
            {getDayName(effectiveAt)}, {getFormattedDate(effectiveAt)}
          </span>
        </div>
      )}
    </div>
  );
}
